import React from 'react';
import { useChartTheme } from 'src/styles/chartTheme';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { Transform } from '@dnd-kit/utilities';
import { CSS } from '@dnd-kit/utilities';
import CardContainer from 'src/components/layouts/CardContainer/CardContainer';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import LineChart from 'src/components/charts/LineChart/LineChart';
import { DashboardTimeSeriesDiagramKpi } from 'src/services/kpi';
import KPINumber from 'src/components/ui/KPINumber/KPINumber';
import styles from './DashboardTimeSeriesDiagramKpiCard.module.scss';

function getYAxisMax(data: number[]): number {
  if (data.length === 0) return 10;

  const maxValue = Math.max(...data);

  if (maxValue <= 10) return 10;

  const magnitude = 10 ** Math.floor(Math.log10(maxValue));
  const normalized = maxValue / magnitude;

  let rounded: number;

  if (normalized <= 1) rounded = 1;
  else if (normalized <= 2) rounded = 2;
  else if (normalized <= 5) rounded = 5;
  else rounded = 10;

  return rounded * magnitude;
}

export interface DashboardTimeSeriesDiagramKpiCardProps {
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  setNodeRef?: (node: HTMLElement | null) => void;
  transform?: Transform | null;
  transition?: string;
  data: DashboardTimeSeriesDiagramKpi;
  title?: string;
  children?: React.ReactNode;
}

const DashboardTimeSeriesDiagramKpiCard: React.FC<DashboardTimeSeriesDiagramKpiCardProps> = ({
  attributes,
  listeners,
  setNodeRef,
  transform,
  transition,
  data,
  title,
  children,
}) => {
  const { t, i18n } = useTranslation();
  const style = {
    transform: CSS.Transform.toString(transform || null),
    transition,
    touchAction: 'none',
  };
  const chartTheme = useChartTheme();
  const defaultColors = [chartTheme.series[0], chartTheme.series[3]];

  const series = data.yLabels.map((_, i) => data.lines.reduce((sum, line) => sum + (line.data[i] ?? 0), 0));
  const total = series.reduce((a, b) => a + b, 0);
  const avg = series.length ? (total / series.length).toLocaleString(i18n.language, { maximumFractionDigits: 1 }) : '0';
  const peakIdx = series.indexOf(Math.max(...series, 0));

  const dataSets = data.lines.map(line => {
    return {
      data: line.data.map(n => (n === null ? 0 : n)),
      borderColor: line.color || defaultColors[data.lines.indexOf(line) % defaultColors.length],
    };
  });

  const lineData = {
    labels: data.yLabels,
    datasets: dataSets,
  };

  return (
    <CardContainer
      className={classNames(styles.root)}
      ref={setNodeRef}
      style={style}
      label={title || data.title}
      attributes={attributes}
      listeners={listeners}
      actionChildren={
        total > 0 ? (
          <span className="typo-text-12 text-faded whitespace-nowrap">
            {t('dashboard.charts.footer_stats', {
              avg,
              peak: data.yLabels[peakIdx],
              peakValue: series[peakIdx] || 0,
              total,
            })}
          </span>
        ) : undefined
      }>
      <div className="mb-20">
        {data.progress && (
          <KPINumber
            value={data.totals.toString()}
            label=""
            percentage={Number.parseFloat(data.progress.replaceAll('%', '').trim())}
          />
        )}
      </div>

      <LineChart
        data={lineData}
        options={{
          scales: {
            y: {
              min: 0,
              max: getYAxisMax(dataSets.map(dataSet => dataSet.data).flat()),
            },
          },
        }}
      />
      {children}
    </CardContainer>
  );
};

export default DashboardTimeSeriesDiagramKpiCard;
